import { useRef, useState } from 'react';
import { Head, useForm, usePage, router } from '@inertiajs/react';
import axios from 'axios';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import FormTextarea from '@/Components/FormTextarea';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import {
    ArrowLeftIcon,
    PhotoIcon,
    PlusIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

export default function Create({ waterUnits: availableWaterUnits, flash }) {
    const { auth } = usePage().props;
    const initialWaterUnits = availableWaterUnits?.length
        ? availableWaterUnits.map(u => ({
            unit_number: String(u.unit_number || u.id),
            unit_name: u.name || u.unit_name || '',
            toe: '',
            ph: '',
            product: '',
            reject: '',
            pbm: '',
            pam: '',
        }))
        : [];

    const { data, setData, post, processing, errors, setError, transform } = useForm({
        date: new Date().toISOString().split('T')[0],
        foreman_name: auth?.user?.name || '',
        work_description: '',
        machinery_report: '',
        machinery_attachment: null,
        plants_report: '',
        plants_images: [],
        status: 'draft',
        water_units: initialWaterUnits,
        water_unit_images: {},
    });

    const [chunkedProcessing, setChunkedProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ total: 0, done: 0 });
    const statusRef = useRef('draft');

    transform((formData) => ({
        ...formData,
        status: statusRef.current,
    }));

    const BATCH_SIZE = 15;

    const [uploadError, setUploadError] = useState(null);

    const getCsrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const axiosPost = async (url, formData, extraHeaders = {}) => {
        const token = getCsrf();
        const res = await axios.post(url, formData, {
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...(token ? { 'X-CSRF-TOKEN': token } : {}),
                ...extraHeaders,
            },
            timeout: 120000,
        });
        return res.data;
    };

    const updateWaterUnit = (i, field, val) => {
        setData(prev => {
            const units = [...prev.water_units];
            units[i] = { ...units[i], [field]: val };
            return { ...prev, water_units: units };
        });
    };

    const handleWaterUnitImages = (i, files) => {
        const newFiles = Array.from(files);
        setData(prev => ({
            ...prev,
            water_unit_images: {
                ...prev.water_unit_images,
                [i]: [...(prev.water_unit_images[i] || []), ...newFiles],
            },
        }));
    };

    const removeWaterUnitImage = (unitIdx, imgIdx) => {
        setData(prev => {
            const imgs = [...(prev.water_unit_images[unitIdx] || [])];
            imgs.splice(imgIdx, 1);
            return {
                ...prev,
                water_unit_images: {
                    ...prev.water_unit_images,
                    [unitIdx]: imgs,
                },
            };
        });
    };

    const submitAs = async (status) => {
        statusRef.current = status;

        const plantsFiles = data.plants_images.filter((f) => f instanceof File);
        const waterUnitFilesMap = {};
        let totalFiles = plantsFiles.length;
        data.water_units.forEach((unit, i) => {
            const files = (data.water_unit_images[i] || []).filter((f) => f instanceof File);
            if (files.length > 0) {
                waterUnitFilesMap[i] = files;
                totalFiles += files.length;
            }
        });
        const hasNewImages = totalFiles > 0;

        if (!hasNewImages) {
            post('/daily-reports', { forceFormData: true });
            return;
        }

        const totalBatches =
            Math.ceil(plantsFiles.length / BATCH_SIZE) +
            Object.values(waterUnitFilesMap).reduce((sum, files) => sum + Math.ceil(files.length / BATCH_SIZE), 0);

        setChunkedProcessing(true);
        setUploadProgress({ total: totalBatches, done: 0 });
        setUploadError(null);

        let reportId = null;
        try {
            const fd = new FormData();
            fd.append('date', data.date || '');
            fd.append('foreman_name', data.foreman_name || '');
            fd.append('work_description', data.work_description || '');
            fd.append('machinery_report', data.machinery_report || '');
            fd.append('plants_report', data.plants_report || '');
            fd.append('status', status);
            if (data.machinery_attachment instanceof File) {
                fd.append('machinery_attachment', data.machinery_attachment);
            }
            data.water_units.forEach((unit, i) => {
                fd.append(`water_units[${i}][unit_number]`, unit.unit_number || '');
                fd.append(`water_units[${i}][unit_name]`, unit.unit_name || '');
                fd.append(`water_units[${i}][toe]`, unit.toe || '');
                fd.append(`water_units[${i}][ph]`, unit.ph || '');
                fd.append(`water_units[${i}][product]`, unit.product || '');
                fd.append(`water_units[${i}][reject]`, unit.reject || '');
                fd.append(`water_units[${i}][pbm]`, unit.pbm || '');
                fd.append(`water_units[${i}][pam]`, unit.pam || '');
            });

            const json = await axiosPost('/daily-reports', fd, { 'X-Chunked-Upload': '1' });

            if (!json.report_id) {
                setUploadError(json.message || 'Server did not return report_id');
                return;
            }

            reportId = json.report_id;
            let savedCount = 0;

            for (let i = 0; i < plantsFiles.length; i += BATCH_SIZE) {
                const batch = plantsFiles.slice(i, i + BATCH_SIZE);
                const batchFd = new FormData();
                batchFd.append('section', 'plants');
                batch.forEach((f) => batchFd.append('files[]', f));
                const res = await axiosPost(`/daily-reports/${reportId}/add-images`, batchFd);
                savedCount += res.added || batch.length;
                setUploadProgress(prev => ({ ...prev, done: prev.done + 1 }));
            }

            for (const [i, unitFiles] of Object.entries(waterUnitFilesMap)) {
                const unitNumber = data.water_units[i].unit_number;
                for (let j = 0; j < unitFiles.length; j += BATCH_SIZE) {
                    const batch = unitFiles.slice(j, j + BATCH_SIZE);
                    const batchFd = new FormData();
                    batchFd.append('unit_number', String(unitNumber));
                    batch.forEach((f) => batchFd.append('files[]', f));
                    const res = await axiosPost(`/daily-reports/${reportId}/add-images`, batchFd);
                    savedCount += res.added || batch.length;
                    setUploadProgress(prev => ({ ...prev, done: prev.done + 1 }));
                }
            }

            if (savedCount < totalFiles) {
                setUploadError(`Only ${savedCount} of ${totalFiles} images were saved. Please check and re-upload missing photos.`);
                return;
            }

            router.visit('/daily-reports', { preserveState: false });
        } catch (err) {
            const errData = err.response?.data;
            let errMsg = 'Upload failed';
            if (err.response?.status === 419) {
                errMsg = 'Session expired (CSRF). Please refresh the page and try again.';
            } else if (err.response?.status === 413) {
                errMsg = 'File too large. Please reduce image sizes and try again.';
            } else if (err.response?.status === 422 && errData?.errors) {
                errMsg = Object.values(errData.errors).flat().join(', ');
            } else if (errData?.message) {
                errMsg = errData.message;
            } else if (err.message) {
                errMsg = err.message;
            }
            if (reportId) {
                errMsg += ` (Report #${reportId} was created — edit it to re-upload photos.)`;
            }
            console.error('Upload error:', err.response?.status, errMsg, err);
            setUploadError(errMsg);
        } finally {
            setChunkedProcessing(false);
            setUploadProgress({ total: 0, done: 0 });
        }
    };

    return (
        <AppLayout>
            <Head title="New Daily Report" />
            <PageHeader
                title="New Daily Report"
                subtitle="Record today's field operations"
                actions={
                    <Button href="/daily-reports" variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}
            {uploadError && <Alert type="danger" className="mb-4">{uploadError}</Alert>}
            {errors?.message && <Alert type="danger" className="mb-4">{errors.message}</Alert>}

            {chunkedProcessing && uploadProgress.total > 0 && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-800">
                            Uploading images… {uploadProgress.done}/{uploadProgress.total} batches
                        </span>
                        <span className="text-xs text-blue-600">
                            {Math.round((uploadProgress.done / uploadProgress.total) * 100)}%
                        </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }}
                        />
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Please do not close or navigate away from this page.</p>
                </div>
            )}

            <form onSubmit={e => e.preventDefault()}>
                {/* General Info */}
                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">General Information</h3>
                    </div>
                    <CardBody className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormInput
                                label="Date"
                                type="date"
                                value={data.date}
                                onChange={e => setData('date', e.target.value)}
                                error={errors.date}
                                required
                            />
                            <FormInput
                                label="Foreman Name"
                                value={data.foreman_name}
                                readOnly
                                className="bg-slate-50 cursor-not-allowed"
                            />
                        </div>
                        <FormTextarea
                            label="Work Description"
                            value={data.work_description}
                            onChange={e => setData('work_description', e.target.value)}
                            error={errors.work_description}
                            rows={4}
                            placeholder="Describe the work done today..."
                        />
                    </CardBody>
                </Card>

                {/* Machinery */}
                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                        <WrenchScrewdriverIcon className="w-5 h-5 text-slate-400" />
                        <h3 className="font-semibold text-slate-800">Machinery</h3>
                    </div>
                    <CardBody className="space-y-4">
                        <FormTextarea
                            label="Machinery Report"
                            value={data.machinery_report}
                            onChange={e => setData('machinery_report', e.target.value)}
                            error={errors.machinery_report}
                            rows={3}
                            placeholder="Machinery usage details..."
                        />
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Machinery Attachment</label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                onChange={e => setData('machinery_attachment', e.target.files[0] || null)}
                                className="text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                            />
                            {errors.machinery_attachment && <p className="mt-1 text-xs text-red-500">{errors.machinery_attachment}</p>}
                        </div>
                    </CardBody>
                </Card>

                {/* Plants */}
                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Plants</h3>
                    </div>
                    <CardBody className="space-y-4">
                        <FormTextarea
                            label="Plants Report"
                            value={data.plants_report}
                            onChange={e => setData('plants_report', e.target.value)}
                            error={errors.plants_report}
                            rows={3}
                            placeholder="Plants activity and status..."
                        />
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Plants Photos</label>

                            {data.plants_images.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {data.plants_images.map((file, fi) => (
                                        <div key={fi} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={file.name}
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setData(prev => ({ ...prev, plants_images: prev.plants_images.filter((_, idx) => idx !== fi) }));
                                                }}
                                                className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition shadow"
                                            >
                                                &times;
                                            </button>
                                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[7px] px-1 truncate text-center">
                                                {file.name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <label className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors block">
                                <PhotoIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                                <p className="text-sm text-slate-500 mb-1">
                                    {data.plants_images.length > 0
                                        ? `${data.plants_images.length} photo(s) selected — click to add more`
                                        : 'Click to upload plant photos'}
                                </p>
                                <p className="text-xs text-slate-400">You can click multiple times to keep adding photos</p>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={e => {
                                        if (e.target.files.length > 0) {
                                            const newFiles = Array.from(e.target.files);
                                            setData(prev => ({ ...prev, plants_images: [...prev.plants_images, ...newFiles] }));
                                        }
                                        e.target.value = '';
                                    }}
                                    className="hidden"
                                />
                            </label>
                            {errors.plants_images && <p className="mt-2 text-xs text-red-500">{errors.plants_images}</p>}
                        </div>
                    </CardBody>
                </Card>

                {/* Water Units */}
                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Water Units</h3>
                    </div>
                    <CardBody>
                        <div className="space-y-6">
                            {data.water_units.map((unit, i) => (
                                <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                                    <div className="mb-3">
                                        <span className="text-sm font-semibold text-slate-800">Unit {unit.unit_number || i + 1}</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <FormInput
                                            label="TOE"
                                            value={unit.toe}
                                            onChange={e => updateWaterUnit(i, 'toe', e.target.value)}
                                            placeholder="TOE"
                                        />
                                        <FormInput
                                            label="pH"
                                            value={unit.ph}
                                            onChange={e => updateWaterUnit(i, 'ph', e.target.value)}
                                            placeholder="pH"
                                        />
                                        <FormInput
                                            label="Product"
                                            value={unit.product}
                                            onChange={e => updateWaterUnit(i, 'product', e.target.value)}
                                            placeholder="Product"
                                        />
                                        <FormInput
                                            label="Reject"
                                            value={unit.reject}
                                            onChange={e => updateWaterUnit(i, 'reject', e.target.value)}
                                            placeholder="Reject"
                                        />
                                        <FormInput
                                            label="PBM"
                                            value={unit.pbm}
                                            onChange={e => updateWaterUnit(i, 'pbm', e.target.value)}
                                            placeholder="PBM"
                                        />
                                        <FormInput
                                            label="PAM"
                                            value={unit.pam}
                                            onChange={e => updateWaterUnit(i, 'pam', e.target.value)}
                                            placeholder="PAM"
                                        />
                                    </div>
                                    <div className="mt-3">
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Unit Photos</label>
                                        <div className="flex flex-wrap gap-2">
                                            {(data.water_unit_images[i] || []).map((file, fi) => (
                                                <div key={fi} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                                                    <img
                                                        src={URL.createObjectURL(file)}
                                                        alt={file.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeWaterUnitImage(i, fi)}
                                                        className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                                                    >
                                                        &times;
                                                    </button>
                                                    <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[7px] px-1 truncate text-center">
                                                        {file.name}
                                                    </div>
                                                </div>
                                            ))}
                                            <label className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-600 flex flex-col items-center justify-center cursor-pointer transition bg-white hover:bg-emerald-50">
                                                <PlusIcon className="w-6 h-6 text-slate-400" />
                                                <span className="text-[9px] text-slate-400 mt-0.5">Add Photo</span>
                                                <input
                                                    type="file"
                                                    multiple
                                                    accept="image/*"
                                                    onChange={e => { handleWaterUnitImages(i, e.target.files); e.target.value = ''; }}
                                                    className="hidden"
                                                />
                                            </label>
                                        </div>
                                        {errors[`water_unit_images.${i}`] && (
                                            <p className="mt-1 text-xs text-red-500">{errors[`water_unit_images.${i}`]}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {errors.water_units && <p className="mt-2 text-xs text-red-500">{errors.water_units}</p>}
                    </CardBody>
                </Card>

                <div className="flex gap-3">
                    <Button type="button" variant="secondary" disabled={processing || chunkedProcessing} onClick={() => submitAs('draft')}>
                        {chunkedProcessing
                            ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
                            : processing ? 'Creating…' : 'Create as Draft'}
                    </Button>
                    <Button type="button" disabled={processing || chunkedProcessing} onClick={() => submitAs('final')}>
                        {chunkedProcessing
                            ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
                            : processing ? 'Creating…' : 'Create as Final'}
                    </Button>
                    <Button href="/daily-reports" variant="outline" disabled={chunkedProcessing}>Cancel</Button>
                </div>
            </form>
        </AppLayout>
    );
}
